/** Max pixels per side accepted by outpaint providers. */
export const OUTPAINT_MAX_MARGIN = 2000;

/**
 * Minimal symmetric expansion so (w+left+right):(h+up+down) matches targetWidth:targetHeight.
 * @returns {{ left: number, right: number, up: number, down: number }}
 */
export function computeOutpaintMargins(
  naturalW,
  naturalH,
  targetWidth,
  targetHeight
) {
  if (
    !naturalW ||
    !naturalH ||
    !targetWidth ||
    !targetHeight ||
    naturalW <= 0 ||
    naturalH <= 0
  ) {
    return { left: 0, right: 0, up: 0, down: 0 };
  }

  const targetRatio = targetWidth / targetHeight;
  const currentRatio = naturalW / naturalH;

  let left = 0;
  let right = 0;
  let up = 0;
  let down = 0;

  if (currentRatio > targetRatio + 1e-9) {
    const newHeight = naturalW / targetRatio;
    const totalV = Math.round(newHeight - naturalH);
    if (totalV > 0) {
      up = Math.floor(totalV / 2);
      down = totalV - up;
    }
  } else if (currentRatio < targetRatio - 1e-9) {
    const newWidth = naturalH * targetRatio;
    const totalH = Math.round(newWidth - naturalW);
    if (totalH > 0) {
      left = Math.floor(totalH / 2);
      right = totalH - left;
    }
  }

  return clampMarginsProportionally({ left, right, up, down }, OUTPAINT_MAX_MARGIN);
}

/**
 * If any side exceeds maxPerSide, scale all four down equally (preserves aspect goal).
 */
export function clampMarginsProportionally(margins, maxPerSide) {
  const { left, right, up, down } = margins;
  const peak = Math.max(left, right, up, down, 0);
  if (peak === 0) return { left: 0, right: 0, up: 0, down: 0 };
  if (peak <= maxPerSide) {
    return {
      left: Math.max(0, Math.round(left)),
      right: Math.max(0, Math.round(right)),
      up: Math.max(0, Math.round(up)),
      down: Math.max(0, Math.round(down)),
    };
  }
  const scale = maxPerSide / peak;
  return {
    left: Math.max(0, Math.round(left * scale)),
    right: Math.max(0, Math.round(right * scale)),
    up: Math.max(0, Math.round(up * scale)),
    down: Math.max(0, Math.round(down * scale)),
  };
}

const ZERO_EXPAND = { left: 0, right: 0, up: 0, down: 0 };

/**
 * Hide expansion gutters only while the finished result card is selected.
 * Original, and a preview that is still the source, keep the margins.
 */
export function outpaintStageExpand({ resultImageUrl, stageKey, expand }) {
  const showingResult = Boolean(resultImageUrl && stageKey === "preview");
  return showingResult ? { ...ZERO_EXPAND } : expand;
}

/** While a result exists, margin edits compose on the original, not the result. */
export function outpaintComposeStageKey(hasResult) {
  return hasResult ? "original" : "preview";
}

export function hasPositiveExpand(expand) {
  return (
    (expand?.left ?? 0) > 0 ||
    (expand?.right ?? 0) > 0 ||
    (expand?.up ?? 0) > 0 ||
    (expand?.down ?? 0) > 0
  );
}
